import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ArrowLeft, CalendarIcon, Download, RefreshCw, Save, Loader2, Check, Lock, Trash2, ShoppingCart, Sparkles, LayoutGrid, List, X, Search } from 'lucide-react';
import { toast } from 'sonner';
import RecipeImage from '../components/RecipeImage';
import { format, addDays, startOfWeek, parseISO, isBefore, startOfDay } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
// Order a day is actually eaten in. Breakfast and snack were added so a menu can mirror a
// nutrition plan; older menus have no slots of those types and simply render empty.
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner_main', 'dinner_secondary', 'snack'];

// Derived from MEAL_TYPES on purpose: the list view used to hardcode 3 columns, so adding a
// meal type silently dropped the extras off the grid. This is an inline style rather than a
// Tailwind class because Tailwind only emits CSS for class strings it can read literally in
// the source — an interpolated `grid-cols-[...]` would compile to nothing.
const LIST_GRID_STYLE = { gridTemplateColumns: `140px repeat(${MEAL_TYPES.length}, 1fr)` };

const mealLabel = (t, mealType) => ({
  breakfast: t('dashboard.breakfast'),
  lunch: t('dashboard.lunch'),
  dinner_main: t('dashboard.dinnerMain'),
  dinner_secondary: t('dashboard.dinnerSecondary'),
  snack: t('dashboard.snack'),
}[mealType] || mealType);

export default function MenuEditorPage() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { menuId } = useParams();
  const isNew = menuId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [menu, setMenu] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [isEditable, setIsEditable] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    week_start: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    avoid_consecutive_category: true,
    avoid_consecutive_recipe: true,
  });

  // View mode: 'mosaic' | 'list'
  const [viewMode, setViewMode] = useState('mosaic');

  // Slot selection dialog
  const [selectDialogOpen, setSelectDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [recipeSearch, setRecipeSearch] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Shopping List state
  const [shoppingList, setShoppingList] = useState(null);
  const [loadingShoppingList, setLoadingShoppingList] = useState(false);
  const [shoppingListDialogOpen, setShoppingListDialogOpen] = useState(false);

  useEffect(() => {
    fetchRecipes();
    if (!isNew) {
      fetchMenu();
    }
  }, [menuId]);

  const fetchRecipes = async () => {
    try {
      const response = await fetch(`${API}/recipes`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRecipes(data);
      }
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  // MOCK DATA FOR TUTORIAL
  const MOCK_MENU = {
    menu_id: 'demo',
    title: 'Demo Menu',
    week_start: new Date().toISOString().split('T')[0],
    week_end: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    total_calories: 14500,
    slots: DAYS.flatMap(day => [
      { day, meal_type: 'lunch', recipe_name: 'Grilled Salmon with Asparagus', calories: 650, recipe_image: 'https://images.pexels.com/photos/3763847/pexels-photo-3763847.jpeg', recipe_id: 'mock-1' },
      { day, meal_type: 'dinner_main', recipe_name: 'Quinoa Salad Bowl', calories: 450, recipe_image: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg', recipe_id: 'mock-2' },
      { day, meal_type: 'dinner_secondary', recipe_name: 'Greek Yogurt Parfait', calories: 250, recipe_image: 'https://images.pexels.com/photos/1099680/pexels-photo-1099680.jpeg', recipe_id: 'mock-3' }
    ])
  };

  const fetchMenu = async () => {
    // Handle mock demo menu
    if (menuId === 'demo') {
      setMenu(MOCK_MENU);
      setFormData({
        title: MOCK_MENU.title,
        week_start: MOCK_MENU.week_start,
      });
      setIsEditable(false); // Demo menu is read-only
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/menus/${menuId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMenu(data);
        setFormData({
          title: data.title,
          week_start: data.week_start,
        });

        // Check if menu is editable (week_end is not in the past)
        const weekEnd = parseISO(data.week_end);
        const today = startOfDay(new Date());
        setIsEditable(!isBefore(weekEnd, today));
      } else {
        toast.error(t('errors.notFound'));
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const generateMenu = async () => {
    if (recipes.length === 0) {
      toast.error(t('errors.noRecipes'));
      navigate('/recipes/new');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('El título es requerido');
      return;
    }

    setGenerating(true);

    try {
      const response = await fetch(`${API}/menus/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: formData.title,
          week_start: formData.week_start,
          avoid_consecutive_category: formData.avoid_consecutive_category,
          avoid_consecutive_recipe: formData.avoid_consecutive_recipe,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setMenu(data);
        toast.success(t('success.saved'));
        // Navigate to the created menu
        navigate(`/menu/${data.menu_id}`, { replace: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error('Error generating menu:', error);
      toast.error(t('errors.generic'));
    } finally {
      setGenerating(false);
    }
  };

  const updateMenu = async () => {
    if (!menu || !isEditable) return;

    setSaving(true);

    try {
      const response = await fetch(`${API}/menus/${menu.menu_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: formData.title,
          slots: menu.slots,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setMenu(data);
        toast.success(t('success.saved'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error('Error updating menu:', error);
      toast.error(t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const deleteMenu = async () => {
    if (!menu) return;

    setDeleting(true);
    try {
      const response = await fetch(`${API}/menus/${menu.menu_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success(t('success.deleted'));
        navigate('/dashboard');
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error deleting menu:', error);
      toast.error(t('errors.generic'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const downloadImage = async () => {
    if (!menu) return;

    // Prevent for demo menu
    if (menu.menu_id === 'demo') {
      toast.info(language === 'es' ? 'Función no disponible en modo demo' : 'Function not available in demo mode');
      return;
    }

    try {
      const response = await fetch(`${API}/menus/${menu.menu_id}/image`, {
        credentials: 'include',
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `menu_${menu.menu_id}.png`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(t('menu.published'));
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error(t('errors.generic'));
    }
  };

  const fetchShoppingList = async () => {
    if (!menu) return;

    // Prevent for demo menu
    if (menu.menu_id === 'demo') {
      toast.info(language === 'es' ? 'Función no disponible en modo demo' : 'Function not available in demo mode');
      return;
    }

    // Set states immediately
    setShoppingList(null);
    setLoadingShoppingList(true);
    setShoppingListDialogOpen(true);

    // We use a small timeout to "break" the execution and let the browser
    // render the loading states before we start the long 4s wait.
    setTimeout(async () => {
      try {
        // Deliberate 4-second delay as requested
        await new Promise(resolve => setTimeout(resolve, 4000));

        const response = await fetch(`${API}/menus/${menu.menu_id}/shopping-list`, {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          const savedKey = `shopping_${menu.menu_id}`;
          const saved = JSON.parse(localStorage.getItem(savedKey) || '{}');
          const ingredientsWithStatus = data.ingredients.map(ing => ({
            ...ing,
            purchased: saved[ing.name] === true
          }));
          setShoppingList(ingredientsWithStatus);
        } else {
          toast.error(t('errors.generic'));
          setShoppingListDialogOpen(false);
          setLoadingShoppingList(false);
        }
      } catch (error) {
        console.error('Error fetching shopping list:', error);
        toast.error(t('errors.generic'));
        setShoppingListDialogOpen(false);
        setLoadingShoppingList(false);
      } finally {
        setLoadingShoppingList(false);
      }
    }, 0);
  };

  const togglePurchased = (index) => {
    const newList = [...shoppingList];
    newList[index] = { ...newList[index], purchased: !newList[index].purchased };
    setShoppingList(newList);
    // Persist to localStorage
    const savedKey = `shopping_${menu.menu_id}`;
    const saved = {};
    newList.forEach(ing => { if (ing.purchased) saved[ing.name] = true; });
    localStorage.setItem(savedKey, JSON.stringify(saved));
  };

  const getSlot = (day, mealType) => {
    if (!menu?.slots) return null;
    return menu.slots.find(s => s.day === day && s.meal_type === mealType);
  };

  const openSlotSelector = (day, mealType) => {
    if (!isEditable) {
      toast.error(language === 'es'
        ? 'No puedes editar menús históricos. Copia el menú para crear uno nuevo.'
        : 'Cannot edit historical menus. Copy the menu to create a new one.');
      return;
    }
    setRecipeSearch('');
    setSelectedSlot({ day, mealType });
    setSelectDialogOpen(true);
  };

  const selectRecipe = (recipe) => {
    if (!selectedSlot || !menu) return;

    const slotExists = menu.slots.some(
      slot => slot.day === selectedSlot.day && slot.meal_type === selectedSlot.mealType
    );

    let newSlots;
    if (slotExists) {
      newSlots = menu.slots.map(slot => {
        if (slot.day === selectedSlot.day && slot.meal_type === selectedSlot.mealType) {
          return {
            ...slot,
            recipe_id: recipe.recipe_id,
            recipe_name: recipe.name,
            recipe_image: recipe.image,
            calories: recipe.calories,
          };
        }
        return slot;
      });
    } else {
      newSlots = [
        ...menu.slots,
        {
          day: selectedSlot.day,
          meal_type: selectedSlot.mealType,
          recipe_id: recipe.recipe_id,
          recipe_name: recipe.name,
          recipe_image: recipe.image,
          calories: recipe.calories,
        }
      ];
    }

    setMenu({ ...menu, slots: newSlots });
    setSelectDialogOpen(false);
    setSelectedSlot(null);
  };

  const clearSlot = (day, mealType) => {
    if (!isEditable || !menu) return;
    const newSlots = menu.slots.map(slot => {
      if (slot.day === day && slot.meal_type === mealType) {
        return { ...slot, recipe_id: null, recipe_name: null, recipe_image: null, calories: null };
      }
      return slot;
    });
    setMenu({ ...menu, slots: newSlots });
  };

  // Menu meal types and recipe categories share the same five names, so the slot type IS
  // the category to filter by. (This used to be a lookup table that silently returned
  // nothing for any type missing from it.)
  const getRecipesForSlot = (mealType) => recipes.filter(r =>
    (r.categories && r.categories.includes(mealType)) || r.category === mealType
  );

  const dateLocale = language === 'es' ? es : enUS;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-stone hover:text-charcoal -ml-2"
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
          {t('back')}
        </Button>

        {/* Historical Menu Warning */}
        {menu && !isEditable && (
          <Card className="bg-amber-50 border-amber-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="font-manrope font-semibold text-amber-800">
                {language === 'es' ? 'Menú histórico' : 'Historical Menu'}
              </p>
              <p className="font-inter text-sm text-amber-700">
                {language === 'es'
                  ? 'Este menú no se puede editar porque la semana ya terminó.'
                  : 'This menu cannot be edited because the week has ended.'}
              </p>
            </div>
          </Card>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <h1 className="font-manrope font-bold text-3xl text-charcoal">
            {isNew ? t('menu.generate') : t('menu.title')}
          </h1>
          {menu && (
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {user?.plan === 'premium' && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={fetchShoppingList}
                    disabled={loadingShoppingList}
                    className="rounded-full px-4 sm:px-6 border-sage text-sage hover:bg-sage/5 min-w-0 sm:min-w-[140px]"
                    data-testid="shopping-list-button"
                  >
                    {loadingShoppingList ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ShoppingCart className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    )}
                    {language === 'es' ? 'Lista compra' : 'Shopping List'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadImage}
                    className="rounded-full px-4 sm:px-6"
                    data-testid="download-menu-image-button"
                  >
                    <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {t('menu.publish')}
                  </Button>
                </div>
              )}
              {isEditable && (
                <div className="flex gap-2">
                  {!isNew && (
                    <Button
                      variant="outline"
                      onClick={() => setDeleteDialogOpen(true)}
                      className="rounded-full px-4 text-destructive hover:text-destructive border-border hover:bg-destructive/5"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    onClick={updateMenu}
                    disabled={saving}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-5 sm:px-6"
                    data-testid="save-menu-button"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    )}
                    {t('save')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form for New Menu */}
        {isNew && !menu && (
          <Card className="bg-white rounded-3xl shadow-soft p-6 md:p-8 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Title */}
              <div className="space-y-2">
                <Label className="font-manrope font-semibold text-charcoal">
                  {t('menu.form.title')} *
                </Label>
                <Input
                  data-testid="menu-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('menu.form.titlePlaceholder')}
                  className="h-12 rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter"
                />
              </div>

              {/* Week Start */}
              <div className="space-y-2">
                <Label className="font-manrope font-semibold text-charcoal">
                  {t('menu.form.weekStart')}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-start rounded-xl border-border bg-white/50 font-inter"
                      data-testid="menu-week-start-button"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2 text-stone" strokeWidth={1.5} />
                      {format(new Date(formData.week_start), 'PPP', { locale: dateLocale })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={new Date(formData.week_start)}
                      onSelect={(date) => date && setFormData({ ...formData, week_start: format(date, 'yyyy-MM-dd') })}
                      locale={dateLocale}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Generation options */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <Checkbox
                  checked={formData.avoid_consecutive_category}
                  onCheckedChange={(checked) => setFormData({ ...formData, avoid_consecutive_category: !!checked })}
                />
                <div>
                  <p className="font-manrope font-semibold text-sm text-charcoal">
                    {language === 'es' ? 'Evitar mismo tipo de alimento seguido' : 'Avoid same food type on consecutive days'}
                  </p>
                  <p className="font-inter text-xs text-stone">
                    {language === 'es'
                      ? 'No pondrá la misma categoría (ej. verduras) dos días seguidos en el mismo tipo de comida.'
                      : 'Won\'t place the same category (e.g. vegetables) two days in a row for the same meal.'}
                  </p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <Checkbox
                  checked={formData.avoid_consecutive_recipe}
                  onCheckedChange={(checked) => setFormData({ ...formData, avoid_consecutive_recipe: !!checked })}
                />
                <div>
                  <p className="font-manrope font-semibold text-sm text-charcoal">
                    {language === 'es' ? 'Evitar misma receta seguida' : 'Avoid same recipe on consecutive days'}
                  </p>
                  <p className="font-inter text-xs text-stone">
                    {language === 'es'
                      ? 'No repetirá la misma receta dos días seguidos.'
                      : 'Won\'t repeat the same recipe two days in a row.'}
                  </p>
                </div>
              </label>
            </div>

            <Button
              onClick={generateMenu}
              disabled={generating || recipes.length === 0}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-6 font-manrope font-bold shadow-lg hover:shadow-xl transition-all active:scale-95"
              data-testid="generate-menu-button"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('menu.generating')}
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  {t('menu.generate')}
                </>
              )}
            </Button>

            {recipes.length === 0 && (
              <p className="text-center text-stone mt-4 font-inter">
                {t('errors.noRecipes')}
              </p>
            )}
          </Card>
        )}

        {/* Menu Grid */}
        {menu && (
          <>
            {/* Title input for existing menu */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <Input
                  data-testid="menu-title-edit-input"
                  value={formData.title}
                  onChange={(e) => isEditable && setFormData({ ...formData, title: e.target.value })}
                  readOnly={!isEditable}
                  className={`text-xl font-manrope font-semibold h-12 rounded-xl border-border flex-1 ${isEditable ? 'bg-white/50' : 'bg-muted/50 cursor-not-allowed'}`}
                />
                {/* View mode toggle (desktop only) */}
                <div className="hidden md:flex items-center gap-1 bg-muted/50 rounded-xl p-1 flex-shrink-0">
                  <button
                    onClick={() => setViewMode('mosaic')}
                    title={language === 'es' ? 'Vista mosaico' : 'Mosaic view'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-inter transition-all ${viewMode === 'mosaic'
                      ? 'bg-white shadow-sm text-charcoal font-medium'
                      : 'text-stone hover:text-charcoal'
                      }`}
                  >
                    <LayoutGrid className="w-4 h-4" strokeWidth={1.5} />
                    {language === 'es' ? 'Mosaico' : 'Mosaic'}
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    title={language === 'es' ? 'Vista listado' : 'List view'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-inter transition-all ${viewMode === 'list'
                      ? 'bg-white shadow-sm text-charcoal font-medium'
                      : 'text-stone hover:text-charcoal'
                      }`}
                  >
                    <List className="w-4 h-4" strokeWidth={1.5} />
                    {language === 'es' ? 'Listado' : 'List'}
                  </button>
                </div>
              </div>
              <p className="text-sm text-stone mt-2 font-inter">
                {menu.week_start && format(parseISO(menu.week_start), 'dd/MM/yyyy')} - {menu.week_end && format(parseISO(menu.week_end), 'dd/MM/yyyy')}
              </p>
            </div>

            {/* Desktop: 8-column grid with labels (Mosaic view) */}
            <div className={`${viewMode === 'mosaic' ? 'hidden md:grid' : 'hidden'} grid-cols-[100px_repeat(7,1fr)] gap-3`}>
              {/* Corner Header */}
              <div className="h-full"></div>

              {/* Day Headers */}
              {DAYS.map(day => (
                <div key={day} className="text-center py-3">
                  <p className="font-manrope font-semibold text-sm text-charcoal">
                    {t(`days.${day}`)}
                  </p>
                </div>
              ))}

              {/* Meal Rows */}
              {MEAL_TYPES.map(mealType => {
                const label = mealLabel(t, mealType);
                return (
                  <React.Fragment key={mealType}>
                    {/* Row Label */}
                    <div className="flex flex-col items-end justify-center pr-4 text-right">
                      {mealType.startsWith('dinner') ? (
                        <>
                          <p className="font-manrope font-bold text-[10px] uppercase tracking-widest text-charcoal/80 leading-none mb-1">
                            {label.match(/^(Cena|Dinner)/i)?.[0]}
                          </p>
                          <p className="font-manrope font-bold text-[9px] uppercase tracking-widest text-stone/70 leading-tight">
                            {label.replace(/^(Cena|Dinner)\s+/i, '')}
                          </p>
                        </>
                      ) : (
                        <p className="font-manrope font-bold text-[10px] uppercase tracking-widest text-stone/70 leading-tight">
                          {label}
                        </p>
                      )}
                    </div>

                    {DAYS.map(day => {
                      const slot = getSlot(day, mealType);
                      return (
                        <div
                          key={`${day}-${mealType}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => openSlotSelector(day, mealType)}
                          onKeyDown={(e) => e.key === 'Enter' && openSlotSelector(day, mealType)}
                          className="group relative min-h-[100px] rounded-2xl border-2 border-dashed border-muted-foreground/20 hover:border-primary/50 hover:bg-primary/5 transition-colors flex flex-col items-center justify-center gap-1 p-3 cursor-pointer bg-white/50"
                          data-testid={`slot-${day}-${mealType}`}
                        >
                          {slot?.recipe_name ? (
                            <>
                              {isEditable && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); clearSlot(day, mealType); }}
                                  className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-muted/80 hover:bg-destructive hover:text-white text-stone flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  title={language === 'es' ? 'Eliminar receta' : 'Remove recipe'}
                                >
                                  <X className="w-3 h-3" strokeWidth={2.5} />
                                </button>
                              )}
                              <RecipeImage
                                src={slot.recipe_image}
                                alt={slot.recipe_name}
                                containerClassName="w-10 h-10 rounded-lg"
                              />
                              <p className="font-inter text-xs text-center text-charcoal line-clamp-2">
                                {slot.recipe_name}
                              </p>
                              {slot.calories && (
                                <p className="font-inter text-[10px] text-sage">
                                  {slot.calories} kcal
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="font-inter text-xs text-stone">{t('menu.empty')}</p>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Desktop: List view */}
            {viewMode === 'list' && (
              <div className="hidden md:block">
                {/* Header row */}
                <div className="grid gap-3 mb-2 px-1" style={LIST_GRID_STYLE}>
                  <div />
                  {MEAL_TYPES.map(mealType => (
                    <div key={mealType} className="text-center">
                      <p className="font-manrope font-bold text-[10px] uppercase tracking-widest text-stone/70">
                        {mealLabel(t, mealType)}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Day rows */}
                <div className="space-y-2">
                  {DAYS.map(day => (
                    <div key={day} className="grid gap-3 items-center" style={LIST_GRID_STYLE}>
                      {/* Day label */}
                      <div className="flex items-center">
                        <p className="font-manrope font-semibold text-sm text-charcoal">
                          {t(`days.${day}`)}
                        </p>
                      </div>

                      {/* Meal slots */}
                      {MEAL_TYPES.map(mealType => {
                        const slot = getSlot(day, mealType);
                        return (
                          <div
                            key={`list-${day}-${mealType}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => openSlotSelector(day, mealType)}
                            onKeyDown={(e) => e.key === 'Enter' && openSlotSelector(day, mealType)}
                            className="group relative flex items-center gap-3 p-3 rounded-2xl border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors bg-white/50 min-h-[72px] w-full cursor-pointer"
                          >
                            <RecipeImage
                              src={slot?.recipe_image}
                              alt={slot?.recipe_name}
                              containerClassName="w-12 h-12 rounded-xl flex-shrink-0"
                            />
                            <div className="flex-1 text-left min-w-0">
                              <p className="font-inter text-sm text-charcoal line-clamp-2">
                                {slot?.recipe_name || <span className="text-stone">{t('menu.empty')}</span>}
                              </p>
                              {slot?.calories && (
                                <p className="font-inter text-xs text-sage mt-0.5">
                                  {slot.calories} kcal
                                </p>
                              )}
                            </div>
                            {slot?.recipe_name && isEditable && (
                              <button
                                onClick={(e) => { e.stopPropagation(); clearSlot(day, mealType); }}
                                className="w-6 h-6 rounded-full bg-muted/80 hover:bg-destructive hover:text-white text-stone flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                title={language === 'es' ? 'Eliminar receta' : 'Remove recipe'}
                              >
                                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile: Vertical List */}
            <div className="md:hidden space-y-4">
              {DAYS.map(day => (
                <Card key={day} className="bg-white rounded-2xl shadow-soft p-4 scroll-optimize">
                  <h3 className="font-manrope font-semibold text-charcoal mb-3">
                    {t(`days.${day}`)}
                  </h3>
                  <div className="space-y-3">
                    {MEAL_TYPES.map(mealType => {
                      const slot = getSlot(day, mealType);
                      return (
                        <div
                          key={mealType}
                          role="button"
                          tabIndex={0}
                          onClick={() => openSlotSelector(day, mealType)}
                          onKeyDown={(e) => e.key === 'Enter' && openSlotSelector(day, mealType)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors min-h-[70px] cursor-pointer"
                          data-testid={`slot-mobile-${day}-${mealType}`}
                        >
                          <RecipeImage
                            src={slot?.recipe_image}
                            alt={slot?.recipe_name}
                            containerClassName="w-12 h-12 rounded-lg"
                          />
                          <div className="flex-1 text-left">
                            {/* This used to fall back to "lunch" for anything that wasn't a
                                dinner, which would mislabel breakfast and snack. */}
                            <p className="font-manrope font-medium text-xs text-stone uppercase tracking-wide">
                              {mealType === 'dinner_main' ? t('dashboard.dinnerMainShort') :
                                mealType === 'dinner_secondary' ? t('dashboard.dinnerSecondaryShort') :
                                  mealLabel(t, mealType)}
                            </p>
                            <p className="font-inter text-sm text-charcoal">
                              {slot?.recipe_name || t('menu.empty')}
                            </p>
                          </div>
                          {slot?.calories && (
                            <span className="font-inter text-xs text-sage">
                              {slot.calories} kcal
                            </span>
                          )}
                          {slot?.recipe_name && isEditable && (
                            <button
                              onClick={(e) => { e.stopPropagation(); clearSlot(day, mealType); }}
                              className="w-7 h-7 rounded-full bg-muted hover:bg-destructive hover:text-white text-stone flex items-center justify-center flex-shrink-0"
                              title={language === 'es' ? 'Eliminar receta' : 'Remove recipe'}
                            >
                              <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </div>

            {/* Total Calories */}
            {menu.total_calories && (
              <div className="mt-6 text-center">
                <p className="font-manrope font-bold text-2xl text-sage">
                  {menu.total_calories} kcal
                </p>
                <p className="font-inter text-sm text-stone">{t('dashboard.weekTotal')}</p>
              </div>
            )}
          </>
        )}

        {/* Recipe Selection Dialog */}
        <Dialog open={selectDialogOpen} onOpenChange={(open) => { setSelectDialogOpen(open); if (!open) setRecipeSearch(''); }}>
          <DialogContent className="rounded-2xl max-h-[80vh] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
              <DialogTitle className="font-manrope text-charcoal mb-3">
                {t('menu.selectRecipe')}
              </DialogTitle>
              {/* Search input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone" strokeWidth={1.5} />
                <input
                  type="text"
                  value={recipeSearch}
                  onChange={(e) => setRecipeSearch(e.target.value)}
                  placeholder={language === 'es' ? 'Buscar receta...' : 'Search recipe...'}
                  className="w-full pl-9 pr-4 py-2 rounded-xl border border-border bg-white/50 font-inter text-sm text-charcoal placeholder:text-stone focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  autoFocus
                />
                {recipeSearch && (
                  <button
                    onClick={() => setRecipeSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                )}
              </div>
            </DialogHeader>

            <div className="overflow-y-auto flex-1 px-6 py-4">
              {(() => {
                if (!selectedSlot) return null;
                const allRecipes = getRecipesForSlot(selectedSlot.mealType);
                const filtered = allRecipes
                  .filter(r => r.name.toLowerCase().includes(recipeSearch.toLowerCase()))
                  .sort((a, b) => a.name.localeCompare(b.name));
                const currentSlot = getSlot(selectedSlot.day, selectedSlot.mealType);

                if (allRecipes.length === 0) {
                  return (
                    <p className="text-center text-stone font-inter py-8">
                      {t('menu.noRecipesForSlot')}
                    </p>
                  );
                }
                if (filtered.length === 0) {
                  return (
                    <p className="text-center text-stone font-inter py-8">
                      {language === 'es' ? 'No hay recetas que coincidan.' : 'No matching recipes.'}
                    </p>
                  );
                }
                return (
                  <div className="space-y-2">
                    {filtered.map(recipe => {
                      const isSelected = currentSlot?.recipe_id === recipe.recipe_id;
                      return (
                        <button
                          key={recipe.recipe_id}
                          onClick={() => selectRecipe(recipe)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                            }`}
                          data-testid={`select-recipe-${recipe.recipe_id}`}
                        >
                          <RecipeImage
                            src={recipe.image}
                            alt={recipe.name}
                            containerClassName="w-14 h-14 rounded-xl"
                          />
                          <div className="flex-1 text-left">
                            <p className="font-manrope font-medium text-charcoal">{recipe.name}</p>
                            {recipe.calories && (
                              <p className="font-inter text-sm text-sage">{recipe.calories} kcal</p>
                            )}
                          </div>
                          {isSelected && (
                            <Check className="w-5 h-5 text-primary" strokeWidth={2} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>

        {/* Shopping List Dialog */}
        <Dialog open={shoppingListDialogOpen} onOpenChange={setShoppingListDialogOpen}>
          <DialogContent className="rounded-2xl max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" />
                {language === 'es' ? 'Lista de la Compra' : 'Shopping List'}
              </DialogTitle>
            </DialogHeader>

            <div className="py-4">
              {loadingShoppingList ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="font-inter text-sm text-stone">
                    {language === 'es' ? 'Consolidando ingredientes...' : 'Consolidating ingredients...'}
                  </p>
                </div>
              ) : shoppingList && shoppingList.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-stone font-inter mb-4 italic">
                    {language === 'es'
                      ? 'Aquí tienes todos los ingredientes necesarios para las recetas de esta semana.'
                      : 'Here are all the ingredients needed for this week\'s recipes.'}
                  </p>
                  <div className="space-y-3">
                    {shoppingList.map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() => togglePurchased(idx)}
                        className={`flex items-start gap-4 p-4 rounded-xl border transition-all cursor-pointer ${item.purchased ? 'bg-sage/5 border-sage/20 opacity-60' : 'bg-white border-border'
                          }`}
                      >
                        <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors flex-shrink-0 mt-0.5 ${item.purchased ? 'bg-sage border-sage' : 'border-border bg-white'
                          }`}>
                          {item.purchased && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                        </div>
                        <div className="flex-1">
                          <p className={`font-manrope font-semibold text-charcoal transition-all ${item.purchased ? 'line-through text-stone' : ''
                            }`}>
                            {item.name}
                          </p>
                          <p className="font-inter text-sm text-stone">
                            {item.quantities.join(', ')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-stone font-inter">
                    {language === 'es' ? 'No hay ingredientes para mostrar.' : 'No ingredients to show.'}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                onClick={() => setShoppingListDialogOpen(false)}
                className="w-full rounded-full"
              >
                {t('close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {t('delete')} "{menu?.title}"
              </DialogTitle>
            </DialogHeader>
            <p className="font-inter text-stone py-4">
              {language === 'es'
                ? '¿Estás seguro de que quieres eliminar este menú? Esta acción no se puede deshacer.'
                : 'Are you sure you want to delete this menu? This action cannot be undone.'}
            </p>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="rounded-full"
              >
                {t('cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={deleteMenu}
                disabled={deleting}
                className="rounded-full"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                {t('delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
